import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, MenuItem, Stack, InputAdornment,
  Avatar, Tooltip, Alert, Skeleton, Card, CardContent, IconButton,
  Pagination, Select, FormControl, Tabs, Tab, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, DialogContentText,
  CircularProgress, Divider, Snackbar,
} from '@mui/material';
import {
  Search, CreditCard, CheckCircle, Cancel, HourglassEmpty,
  ContentCopy, WorkspacePremium, TrendingUp, Paid,
  Refresh, Check, Close, AssignmentReturn,
} from '@mui/icons-material';
import { premiumAPI } from '../services/api';
import { validateRefund } from '../utils/billing';
import { formatCurrency } from '../utils/currency';
import PageHeader from '../components/PageHeader';

const LIMIT = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtAmount(paise) {
  return formatCurrency((paise || 0) / 100);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function truncate(str, n = 20) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

// ── Status configs ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  paid: {
    label: 'Success',
    icon: <CheckCircle sx={{ fontSize: 13 }} />,
    bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0',
  },
  failed: {
    label: 'Failed',
    icon: <Cancel sx={{ fontSize: 13 }} />,
    bg: '#fff1f2', text: '#dc2626', border: '#fecdd3',
  },
  created: {
    label: 'Pending',
    icon: <HourglassEmpty sx={{ fontSize: 13 }} />,
    bg: '#fefce8', text: '#ca8a04', border: '#fef08a',
  },
  cancelled: {
    label: 'Cancelled',
    icon: <Cancel sx={{ fontSize: 13 }} />,
    bg: '#fff1f2', text: '#e11d48', border: '#fecdd3',
  },
  refunded: {
    label: 'Refunded',
    icon: <CheckCircle sx={{ fontSize: 13 }} />,
    bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe',
  },
};

const REFUND_STATUS_CONFIG = {
  requested: {
    label: 'Refund Requested',
    icon: <HourglassEmpty sx={{ fontSize: 13 }} />,
    bg: '#fffbeb', text: '#d97706', border: '#fde68a',
  },
  approved: {
    label: 'Approved (Pending)',
    icon: <CheckCircle sx={{ fontSize: 13 }} />,
    bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe',
  },
  processed: {
    label: 'Refunded',
    icon: <CheckCircle sx={{ fontSize: 13 }} />,
    bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0',
  },
  rejected: {
    label: 'Declined',
    icon: <Cancel sx={{ fontSize: 13 }} />,
    bg: '#fff1f2', text: '#dc2626', border: '#fecdd3',
  },
  none: {
    label: 'No Refund',
    icon: null,
    bg: '#f8fafc', text: '#64748b', border: '#e2e8f0',
  },
};

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        sx={{ p: 0.25, ml: 0.25 }}
      >
        <ContentCopy sx={{ fontSize: 11, color: copied ? '#16a34a' : '#9CA3AF' }} />
      </IconButton>
    </Tooltip>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, color, loading }) {
  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', borderRadius: 2 }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: `${color}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
              {label}
            </Typography>
            {loading
              ? <Skeleton width={60} height={28} />
              : <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, mt: 0.25 }}>{value}</Typography>
            }
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Payments() {
  const [tab, setTab] = useState(0);

  // ── Tab 0: Transactions State ──
  const [rows, setRows]                         = useState([]);
  const [stats, setStats]                       = useState(null);
  const [statsLoading, setStatsLoading]         = useState(true);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [search, setSearch]                     = useState('');
  const [debouncedSearch, setDebouncedSearch]   = useState('');
  const [statusFilter, setStatus]               = useState('all');
  const [page, setPage]                         = useState(1);
  const [pages, setPages]                       = useState(1);
  const [total, setTotal]                       = useState(0);
  const [limit, setLimit]                       = useState(LIMIT);

  // ── Tab 1: Cancellations & Refunds State ──
  const [cancRows, setCancRows]                 = useState([]);
  const [cancStats, setCancStats]               = useState(null);
  const [cancStatsLoading, setCancStatsLoading] = useState(true);
  const [cancLoading, setCancLoading]           = useState(true);
  const [cancError, setCancError]               = useState('');
  const [cancSearch, setCancSearch]             = useState('');
  const [debouncedCancSearch, setDebouncedCancSearch] = useState('');
  const [refundFilter, setRefundFilter]         = useState('all');
  const [cancPage, setCancPage]                 = useState(1);
  const [cancPages, setCancPages]               = useState(1);
  const [cancTotal, setCancTotal]               = useState(0);
  const [cancLimit, setCancLimit]               = useState(LIMIT);

  // ── Refund Action Dialogs ──
  const [processModalOpen, setProcessModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen]   = useState(false);
  const [selectedSub, setSelectedSub]           = useState(null);
  const [actionLoading, setActionLoading]       = useState(false);
  const [refundAmountRupees, setRefundAmountRupees] = useState('');
  const [refundTxId, setRefundTxId]             = useState('');
  const [refundNotes, setRefundNotes]           = useState('');
  const [rejectReason, setRejectReason]         = useState('');
  const [snackbarMsg, setSnackbarMsg]           = useState('');
  const [actionError, setActionError] = useState('');

  // Debounce search input for transactions
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Debounce search input for cancellations
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCancSearch(cancSearch), 400);
    return () => clearTimeout(t);
  }, [cancSearch]);

  // Reset to page 1 when filter / search changes
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch]);
  useEffect(() => { setCancPage(1); }, [refundFilter, debouncedCancSearch]);

  // Load transaction stats
  const loadTransactionStats = useCallback(() => {
    setStatsLoading(true);
    premiumAPI.getTransactionStats()
      .then(r => setStats(r.data.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    loadTransactionStats();
  }, [loadTransactionStats]);

  // Load paginated transactions
  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch)        params.search = debouncedSearch;

      const res = await premiumAPI.getTransactions(params);
      const d   = res.data;
      setRows(d.data   || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, debouncedSearch]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Load cancellations & refunds
  const loadCancellations = useCallback(async () => {
    setCancLoading(true);
    setCancStatsLoading(true);
    setCancError('');
    try {
      const params = { page: cancPage, limit: cancLimit };
      if (refundFilter !== 'all') params.refundStatus = refundFilter;
      if (debouncedCancSearch)   params.search = debouncedCancSearch;

      const res = await premiumAPI.getCancellations(params);
      const d   = res.data;
      setCancRows(d.data || []);
      setCancStats(d.stats || null);
      setCancTotal(d.total || 0);
      setCancPages(d.pages || 1);
    } catch (e) {
      setCancError(e.response?.data?.message || 'Failed to load cancellations');
    } finally {
      setCancLoading(false);
      setCancStatsLoading(false);
    }
  }, [cancPage, cancLimit, refundFilter, debouncedCancSearch]);

  useEffect(() => {
    loadCancellations();
  }, [loadCancellations]);

  // Open Process Refund Dialog
  const handleOpenProcess = (sub) => {
    setActionError('');
    setSelectedSub(sub);
    // Amount is in paise, convert to rupees for input
    const originalRupees = (sub.amount || 0) / 100;
    setRefundAmountRupees(originalRupees.toString());
    setRefundTxId('');
    setRefundNotes('');
    setProcessModalOpen(true);
  };

  // Open Reject Refund Dialog
  const handleOpenReject = (sub) => {
    setActionError('');
    setSelectedSub(sub);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  // Submit Process Refund
  const handleProcessRefundSubmit = async () => {
    if (!selectedSub || actionLoading) return;
    const validation = validateRefund(refundAmountRupees, selectedSub.amount || 0);
    setActionError(validation);
    if (validation) return;
    setActionLoading(true);
    try {
      await premiumAPI.processRefund(selectedSub.id || selectedSub._id, {
        action: 'process',
        refundAmount: Number(refundAmountRupees),
        transactionId: refundTxId.trim(),
        notes: refundNotes.trim() || 'Refund processed by admin',
      });
      setSnackbarMsg('Refund successfully processed and access updated.');
      setProcessModalOpen(false);
      loadCancellations();
      loadRows();
      loadTransactionStats();
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to process refund');
    } finally {
      setActionLoading(false);
    }
  };

  // Submit Reject Refund
  const handleRejectRefundSubmit = async () => {
    if (!selectedSub || actionLoading) return;
    if (!rejectReason.trim()) { setActionError('Enter a reason for declining this refund.'); return; }
    setActionError('');
    setActionLoading(true);
    try {
      await premiumAPI.processRefund(selectedSub.id || selectedSub._id, {
        action: 'reject',
        notes: rejectReason.trim() || 'Refund request declined by administrator',
      });
      setSnackbarMsg('Refund request marked as rejected.');
      setRejectModalOpen(false);
      loadCancellations();
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to reject refund');
    } finally {
      setActionLoading(false);
    }
  };

  // Pagination helpers
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  const cancFrom = cancTotal === 0 ? 0 : (cancPage - 1) * cancLimit + 1;
  const cancTo   = Math.min(cancPage * cancLimit, cancTotal);

  return (
    <Box>
      {/* ── Header ── */}
      <PageHeader
        title="Payments & Subscriptions"
        subtitle="Manage premium subscriptions, incoming transactions, member cancellations, and refund processing."
        gradient="linear-gradient(90deg, #16A34A, #4ADE80)"
      />

      {/* ── Tabs Navigation ── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, newTab) => setTab(newTab)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.95rem',
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<CreditCard sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Transactions & Orders"
          />
          <Tab
            icon={<AssignmentReturn sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Cancellations & Refunds</span>
                {cancStats?.refundsRequested > 0 && (
                  <Chip
                    size="small"
                    label={`${cancStats.refundsRequested} pending`}
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: '#f59e0b',
                      color: '#ffffff',
                    }}
                  />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 0: TRANSACTIONS & ORDERS
      ═══════════════════════════════════════════════════════════════════════ */}
      {tab === 0 && (
        <Box>
          {/* ── Stat cards ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <StatCard loading={statsLoading} icon={<TrendingUp />} label="Total Revenue"
              value={stats ? fmtAmount(stats.revenue) : '—'}
              sub={stats ? `${stats.paid} paid` : ''}
              color="#16a34a" />
            <StatCard loading={statsLoading} icon={<CheckCircle />} label="Successful"
              value={stats?.paid ?? '—'} sub="Payments captured" color="#16a34a" />
            <StatCard loading={statsLoading} icon={<Cancel />} label="Failed"
              value={stats?.failed ?? '—'} sub="Payment failures" color="#dc2626" />
            <StatCard loading={statsLoading} icon={<HourglassEmpty />} label="Pending"
              value={stats?.pending ?? '—'} sub="Orders created" color="#ca8a04" />
          </Box>

          {/* ── Filters ── */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              placeholder="Search by email, name, order ID or transaction ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <TextField
              select label="Status" value={statusFilter}
              onChange={e => setStatus(e.target.value)}
              size="small" sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="paid">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="created">Pending</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </TextField>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* ── Table ── */}
          <TableContainer component={Paper}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
            <Table sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
                  <TableCell sx={{ minWidth: 180 }}>User</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Amount</TableCell>
                  <TableCell sx={{ minWidth: 200, display: { xs: 'none', md: 'table-cell' } }}>Order ID</TableCell>
                  <TableCell sx={{ minWidth: 220, display: { xs: 'none', md: 'table-cell' } }}>Transaction ID</TableCell>
                  <TableCell sx={{ minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>Premium Until</TableCell>
                  <TableCell sx={{ minWidth: 150, display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? [...Array(limit)].map((_, i) => (
                      <TableRow key={i} sx={{ minHeight: 56 }}>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Skeleton variant="circular" width={32} height={32} />
                            <Box><Skeleton width={110} height={13} /><Skeleton width={140} height={11} sx={{ mt: 0.5 }} /></Box>
                          </Stack>
                        </TableCell>
                        <TableCell><Skeleton width={70} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                        <TableCell><Skeleton width={50} height={13} /></TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={140} height={13} /></TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={140} height={13} /></TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={80} height={13} /></TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={100} height={13} /></TableCell>
                      </TableRow>
                    ))
                  : rows.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                          <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
                            <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                              <CreditCard sx={{ fontSize: 40, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
                              {search || statusFilter !== 'all' ? 'No matching transactions' : 'No transactions yet'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
                              {search || statusFilter !== 'all'
                                ? 'Try adjusting your search or filter to find what you\'re looking for.'
                                : 'Premium subscription payments will appear here once users start subscribing.'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  : rows.map(tx => {
                      const sc = STATUS_CONFIG[tx.status] || STATUS_CONFIG.created;
                      return (
                        <TableRow key={tx.id || tx._id}
                          sx={{ '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.15s' }}>

                          {/* User */}
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700 }}>
                                {tx.user?.name?.charAt(0)?.toUpperCase() || '?'}
                              </Avatar>
                              <Box>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="body2" fontWeight={600}>
                                    {tx.user?.name || 'Unknown'}
                                  </Typography>
                                  {tx.user?.isPremium && (
                                    <Tooltip title="Active premium">
                                      <WorkspacePremium sx={{ fontSize: 13, color: '#f0a500' }} />
                                    </Tooltip>
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {tx.user?.email || '—'}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              px: 1, py: 0.25, borderRadius: 1,
                              bgcolor: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                              fontSize: '0.7rem', fontWeight: 700,
                            }}>
                              {sc.icon}{sc.label}
                            </Box>
                          </TableCell>

                          {/* Amount */}
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>{fmtAmount(tx.amount)}</Typography>
                          </TableCell>

                          {/* Order ID */}
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Stack direction="row" alignItems="center">
                              <Tooltip title={tx.razorpayOrderId || '—'}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                  {truncate(tx.razorpayOrderId, 22)}
                                </Typography>
                              </Tooltip>
                              {tx.razorpayOrderId && <CopyButton value={tx.razorpayOrderId} />}
                            </Stack>
                          </TableCell>

                          {/* Transaction ID (Razorpay Payment ID) */}
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {tx.razorpayPaymentId ? (
                              <Stack direction="row" alignItems="center">
                                <Tooltip title={`Razorpay Payment ID: ${tx.razorpayPaymentId}`}>
                                  <Typography
                                    component="a"
                                    href={`https://dashboard.razorpay.com/app/payments/${tx.razorpayPaymentId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="caption"
                                    sx={{
                                      fontFamily: 'monospace', fontWeight: 600,
                                      color: '#1a1a2ed4', textDecoration: 'none',
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                  >
                                    {truncate(tx.razorpayPaymentId, 22)}
                                  </Typography>
                                </Tooltip>
                                <CopyButton value={tx.razorpayPaymentId} />
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>

                          {/* Premium Until */}
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="body2">
                              {tx.status === 'paid' ? fmtDate(tx.endDate) : '—'}
                            </Typography>
                          </TableCell>

                          {/* Date */}
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="caption" color="text.secondary">
                              {fmtDateTime(tx.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {total > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
              sx={{ mt: 2.5, px: 0.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {from}–{to} of {total} transactions
              </Typography>
              <Pagination
                count={pages}
                page={page}
                onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                color="primary"
                size="small"
                siblingCount={1}
                boundaryCount={1}
                showFirstButton
                showLastButton
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">Per page:</Typography>
                <FormControl size="small">
                  <Select
                    value={limit}
                    onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                    sx={{ fontSize: '0.8125rem', minWidth: 70 }}
                  >
                    {[20, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 1: CANCELLATIONS & REFUND TRACKING
      ═══════════════════════════════════════════════════════════════════════ */}
      {tab === 1 && (
        <Box>
          {/* ── Cancellation Stats ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <StatCard
              loading={cancStatsLoading}
              icon={<Cancel />}
              label="Total Cancelled"
              value={cancStats?.totalCancelled ?? '—'}
              sub="Cancelled subscriptions"
              color="#64748b"
            />
            <StatCard
              loading={cancStatsLoading}
              icon={<HourglassEmpty />}
              label="Refunds Requested"
              value={cancStats?.refundsRequested ?? '—'}
              sub="Awaiting admin action"
              color="#d97706"
            />
            <StatCard
              loading={cancStatsLoading}
              icon={<CheckCircle />}
              label="Refunds Processed"
              value={cancStats?.refundsProcessed ?? '—'}
              sub="Completed refunds"
              color="#16a34a"
            />
            <StatCard
              loading={cancStatsLoading}
              icon={<Paid />}
              label="Total Refunded"
              value={cancStats ? fmtAmount(cancStats.totalRefundedAmount) : '—'}
              sub="Processed amount"
              color="#7c3aed"
            />
          </Box>

          {/* ── Cancellation Filters ── */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              placeholder="Search member name, email, phone or order ID…"
              value={cancSearch}
              onChange={e => setCancSearch(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Refund Status"
              value={refundFilter}
              onChange={e => setRefundFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="all">All Refund Statuses</MenuItem>
              <MenuItem value="requested">Pending Review (Requested)</MenuItem>
              <MenuItem value="approved">Approved (Pending Payout)</MenuItem>
              <MenuItem value="processed">Refunded (Processed)</MenuItem>
              <MenuItem value="rejected">Declined (Rejected)</MenuItem>
              <MenuItem value="none">No Refund Requested</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={loadCancellations}
              sx={{ minWidth: 100 }}
            >
              Refresh
            </Button>
          </Stack>

          {cancError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCancError('')}>
              {cancError}
            </Alert>
          )}

          {/* ── Cancellations Table ── */}
          <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}
          >
            <Table sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
                  <TableCell sx={{ minWidth: 180 }}>Member</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Subscription</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Cancellation Info</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Refund Status</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Refund Details</TableCell>
                  <TableCell sx={{ minWidth: 160, textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cancLoading
                  ? [...Array(cancLimit)].map((_, i) => (
                      <TableRow key={i} sx={{ minHeight: 56 }}>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Skeleton variant="circular" width={32} height={32} />
                            <Box><Skeleton width={110} height={13} /><Skeleton width={140} height={11} sx={{ mt: 0.5 }} /></Box>
                          </Stack>
                        </TableCell>
                        <TableCell><Skeleton width={80} height={20} /></TableCell>
                        <TableCell><Skeleton width={120} height={20} /></TableCell>
                        <TableCell><Skeleton width={90} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                        <TableCell><Skeleton width={100} height={20} /></TableCell>
                        <TableCell><Skeleton width={80} height={30} sx={{ ml: 'auto' }} /></TableCell>
                      </TableRow>
                    ))
                  : cancRows.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
                            <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                              <AssignmentReturn sx={{ fontSize: 40, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
                              {cancSearch || refundFilter !== 'all' ? 'No matching cancellations' : 'No cancellation requests'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
                              {cancSearch || refundFilter !== 'all'
                                ? 'Try adjusting your search criteria or status filter.'
                                : 'When members cancel their subscriptions or request refunds from the app, their requests will appear here for processing.'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  : cancRows.map(row => {
                      const user = row.user || row.userId;
                      const rsc = REFUND_STATUS_CONFIG[row.refundStatus] || REFUND_STATUS_CONFIG.none;
                      const isActionable = row.refundStatus === 'requested' || row.refundStatus === 'approved';

                      return (
                        <TableRow
                          key={row.id || row._id}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'background-color 0.15s',
                            bgcolor: row.refundStatus === 'requested' ? '#fffdf7' : 'inherit',
                          }}
                        >
                          {/* Member */}
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 34, height: 34, fontSize: '0.85rem', fontWeight: 700, bgcolor: '#4f46e5' }}>
                                {user?.name?.charAt(0)?.toUpperCase() || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
                                  {user?.name || 'Unknown User'}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    {user?.email || '—'}
                                  </Typography>
                                </Stack>
                                {user?.phone && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {user.phone}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Subscription info */}
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {fmtAmount(row.amount)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.months || 1} Month Premium
                            </Typography>
                            {row.razorpayOrderId && (
                              <Stack direction="row" alignItems="center" sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.7rem' }}>
                                  {truncate(row.razorpayOrderId, 14)}
                                </Typography>
                                <CopyButton value={row.razorpayOrderId} />
                              </Stack>
                            )}
                          </TableCell>

                          {/* Cancellation Info */}
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                              {row.cancellationReason || 'No reason provided'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                              Cancelled: {fmtDateTime(row.cancelledAt || row.updatedAt)}
                            </Typography>
                          </TableCell>

                          {/* Refund Status */}
                          <TableCell>
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              px: 1, py: 0.35, borderRadius: 1.5,
                              bgcolor: rsc.bg, color: rsc.text, border: `1px solid ${rsc.border}`,
                              fontSize: '0.725rem', fontWeight: 700,
                            }}>
                              {rsc.icon}{rsc.label}
                            </Box>
                          </TableCell>

                          {/* Refund Details */}
                          <TableCell>
                            {row.refundStatus === 'processed' ? (
                              <Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: '#16a34a' }}>
                                  {fmtAmount(row.refundAmount || row.amount)}
                                </Typography>
                                {row.refundTransactionId && (
                                  <Stack direction="row" alignItems="center">
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                                      Ref: {truncate(row.refundTransactionId, 14)}
                                    </Typography>
                                    <CopyButton value={row.refundTransactionId} />
                                  </Stack>
                                )}
                                {row.refundNotes && (
                                  <Tooltip title={row.refundNotes}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {row.refundNotes}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            ) : row.refundStatus === 'rejected' ? (
                              <Box>
                                <Typography variant="caption" color="error.main" fontWeight={600} display="block">
                                  Request Declined
                                </Typography>
                                {row.refundNotes && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {row.refundNotes}
                                  </Typography>
                                )}
                              </Box>
                            ) : row.refundStatus === 'requested' ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Awaiting refund action
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>

                          {/* Actions */}
                          <TableCell sx={{ textAlign: 'right' }}>
                            {isActionable ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<Check sx={{ fontSize: 14 }} />}
                                  onClick={() => handleOpenProcess(row)}
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    py: 0.5,
                                    bgcolor: '#16a34a',
                                    '&:hover': { bgcolor: '#15803d' },
                                  }}
                                >
                                  Refund
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Close sx={{ fontSize: 14 }} />}
                                  onClick={() => handleOpenReject(row)}
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    px: 1,
                                    py: 0.5,
                                  }}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            ) : (
                              <Chip
                                size="small"
                                label={row.refundStatus === 'processed' ? 'Settled' : row.refundStatus === 'rejected' ? 'Closed' : 'Logged'}
                                sx={{ fontSize: '0.7rem', color: 'text.secondary', bgcolor: 'action.hover' }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Cancellation Pagination */}
          {cancTotal > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
              sx={{ mt: 2.5, px: 0.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {cancFrom}–{cancTo} of {cancTotal} cancellation records
              </Typography>
              <Pagination
                count={cancPages}
                page={cancPage}
                onChange={(_, v) => { setCancPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                color="primary"
                size="small"
                siblingCount={1}
                boundaryCount={1}
                showFirstButton
                showLastButton
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">Per page:</Typography>
                <FormControl size="small">
                  <Select
                    value={cancLimit}
                    onChange={e => { setCancLimit(Number(e.target.value)); setCancPage(1); }}
                    sx={{ fontSize: '0.8125rem', minWidth: 70 }}
                  >
                    {[20, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          PROCESS REFUND MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={processModalOpen}
        onClose={() => !actionLoading && setProcessModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          Process Member Refund
        </DialogTitle>
        <DialogContent dividers>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <DialogContentText sx={{ mb: 2.5, fontSize: '0.875rem' }}>
            Record a refund already paid through your bank or payment provider. This action does not transfer money. It records the payout and immediately revokes this member’s premium privileges.
          </DialogContentText>

          {selectedSub && (
            <Card sx={{ bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', mb: 3, boxShadow: 'none' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Member Details
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {selectedSub.user?.name || selectedSub.userId?.name || 'Member'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedSub.user?.email || selectedSub.userId?.email || '—'}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Original Paid Amount:</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmtAmount(selectedSub.amount)}</Typography>
                </Stack>
                {selectedSub.cancellationReason && (
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Reason for cancelling:</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedSub.cancellationReason}</Typography>
                  </Stack>
                )}
                {selectedSub.razorpayPaymentId && (
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Razorpay Payment ID:</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {selectedSub.razorpayPaymentId}
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}

          <Stack spacing={2.5}>
            <TextField
              label="Refund Amount (₹)"
              type="number"
              fullWidth
              value={refundAmountRupees}
              onChange={e => setRefundAmountRupees(e.target.value)}
              helperText={`Original charge: ${fmtAmount(selectedSub?.amount)}. Modify if giving a partial refund.`}
              size="small"
              required
            />
            <TextField
              label="Refund Reference / Payment ID"
              placeholder="e.g. rfnd_xxx, Bank UTR, or UPI Reference"
              fullWidth
              value={refundTxId}
              onChange={e => setRefundTxId(e.target.value)}
              helperText="Reference ID from your payment gateway or bank transfer (optional)"
              size="small"
            />
            <TextField
              label="Admin Notes"
              placeholder="e.g. Approved within 48h policy, processed via Razorpay dashboard"
              fullWidth
              multiline
              rows={2}
              value={refundNotes}
              onChange={e => setRefundNotes(e.target.value)}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setProcessModalOpen(false)}
            disabled={actionLoading}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessRefundSubmit}
            variant="contained"
            color="success"
            disabled={actionLoading || !refundAmountRupees}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <Check />}
            sx={{
              fontWeight: 700,
              bgcolor: '#16a34a',
              '&:hover': { bgcolor: '#15803d' },
              px: 2.5,
            }}
          >
            {actionLoading ? 'Processing...' : 'Confirm & Process Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════════
          REJECT REFUND MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => !actionLoading && setRejectModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: 'error.main' }}>
          Decline Refund Request
        </DialogTitle>
        <DialogContent dividers>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <DialogContentText sx={{ mb: 2, fontSize: '0.875rem' }}>
            Are you sure you want to decline this refund request for{' '}
            <strong>{selectedSub?.user?.name || selectedSub?.userId?.name || 'this member'}</strong>?
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            placeholder="e.g. Past 7-day refund window, service already utilized"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            helperText="Provide a clear reason for administrative tracking"
            size="small"
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setRejectModalOpen(false)}
            disabled={actionLoading}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectRefundSubmit}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <Close />}
            sx={{ fontWeight: 700 }}
          >
            {actionLoading ? 'Declining...' : 'Decline Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Notification Feedback ── */}
      <Snackbar
        open={Boolean(snackbarMsg)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMsg('')}
        message={snackbarMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
